import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { SelectOption } from 'src/app/ui/form-field/form-field.component';
import {
  ConditionOperator,
  IConditionFieldDescriptor,
  IEnumOption,
  IWorkflowCondition,
} from '../../models/workflows.models';
import { WorkflowsService } from '../../services/workflows.service';

interface ConditionFieldGroup {
  label: string;
  options: IConditionFieldDescriptor[];
}

const KEYWORD_LIST_OPERATORS: ConditionOperator[] = ['contains_any', 'contains_all'];
const LIST_OPERATORS: ConditionOperator[] = ['in', 'not_in'];

@Component({
  selector: 'app-workflow-condition-row',
  templateUrl: './workflow-condition-row.component.html',
  styleUrls: ['./workflow-condition-row.component.scss'],
})
export class WorkflowConditionRowComponent implements OnInit, OnChanges, OnDestroy {
  @Input() condition!: IWorkflowCondition;
  @Input() conditionFields: IConditionFieldDescriptor[] = [];
  @Input() enumCache: Record<string, IEnumOption[]> = {};
  /** Required by a template-only action on the rule: neither editable nor removable. */
  @Input() locked = false;

  @Output() conditionChanged = new EventEmitter<IWorkflowCondition>();
  @Output() conditionRemoved = new EventEmitter<string>();

  fieldControl = new FormControl<string>('', { nonNullable: true });
  operatorControl = new FormControl<string>('', { nonNullable: true });
  valueControl = new FormControl<unknown>('', { nonNullable: true });
  chipSearchControl = new FormControl<string | IEnumOption>('', { nonNullable: true });

  availableOperators: ConditionOperator[] = [];
  selectedField: IConditionFieldDescriptor | null = null;
  conditionFieldGroups: ConditionFieldGroup[] = [];
  keywordChips: string[] = [];
  selectedMultiEnumOptions: IEnumOption[] = [];
  filteredMultiEnumOptions$: Observable<IEnumOption[]> = of([]);
  hideValue = false;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private workflowsService: WorkflowsService,
  ) {}

  ngOnInit(): void {
    this.fieldControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((fieldKey) => this.onFieldChange(fieldKey));

    this.operatorControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((operator) => this.onOperatorChange(operator));

    this.valueControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onValueChange());

    this.filteredMultiEnumOptions$ = this.chipSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(
        (previous, current) =>
          this.normalizeMultiEnumSearch(previous) === this.normalizeMultiEnumSearch(current)
      ),
      switchMap((value) => this.loadMultiEnumSuggestions(this.normalizeMultiEnumSearch(value))),
      takeUntil(this.destroy$),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['condition'] && this.condition) {
      this.fieldControl.setValue(this.condition.field, { emitEvent: false });
      this.operatorControl.setValue(this.condition.operator, { emitEvent: false });
      this.valueControl.setValue(this.condition.value ?? '', { emitEvent: false });
    }
    if (changes['conditionFields']) {
      this.rebuildConditionFieldGroups();
    }
    if ((changes['condition'] || changes['conditionFields']) && this.condition) {
      this.updateFieldMeta();
      this.syncSelectBackedValue();
      this.emitChangeIfStateDrifted();
    }
    if (changes['enumCache'] && this.condition) {
      this.syncSelectBackedValue();
      this.emitChangeIfStateDrifted();
    }
    if (changes['locked']) {
      this.applyLockedState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFieldChange(fieldKey: string): void {
    this.fieldControl.setValue(fieldKey, { emitEvent: false });
    this.updateFieldMeta();
    const defaultOp = this.availableOperators[0] ?? 'equals';
    this.operatorControl.setValue(defaultOp, { emitEvent: false });
    this.valueControl.setValue(this.getDefaultValueForSelectedField(), { emitEvent: false });
    this.keywordChips = [];
    this.selectedMultiEnumOptions = [];
    this.chipSearchControl.setValue('', { emitEvent: true });
    this.emitChange();
  }

  onOperatorChange(op: string): void {
    const isEnumListOperator =
      this.selectedField?.dataType === 'enum'
      && LIST_OPERATORS.includes(op as ConditionOperator);

    this.hideValue = op === 'is_empty' || op === 'is_not_empty';

    if (this.hideValue) {
      this.keywordChips = [];
      this.valueControl.setValue(null, { emitEvent: false });
    } else if (this.isKeywordListOperatorValue(op)) {
      this.syncKeywordChipsFromValue();
      this.syncKeywordControlValue();
    } else {
      this.keywordChips = [];

      if (isEnumListOperator) {
        this.valueControl.setValue(this.normalizeEnumListValue(this.valueControl.value), { emitEvent: false });
      } else if (this.selectedField?.dataType === 'enum' && Array.isArray(this.valueControl.value)) {
        this.valueControl.setValue(this.getEnumScalarValue(this.valueControl.value), { emitEvent: false });
      } else if (this.selectedField?.dataType !== 'multi_enum' && Array.isArray(this.valueControl.value)) {
        this.valueControl.setValue('', { emitEvent: false });
      }
    }

    this.emitChange();
  }

  onValueChange(): void {
    this.emitChange();
  }

  remove(): void {
    // A locked row has no remove button; this also guards a programmatic call.
    if (this.locked) {
      return;
    }
    this.conditionRemoved.emit(this.condition.id);
  }

  /** Disable the editing controls of a template-locked row.
   *
   *  `emitEvent: false` matters: enabling/disabling a control emits on
   *  valueChanges, which would re-enter onFieldChange and reset the operator/value. */
  private applyLockedState(): void {
    const controls = [
      this.fieldControl,
      this.operatorControl,
      this.valueControl,
      this.chipSearchControl,
    ];
    for (const control of controls) {
      if (this.locked) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
  }

  addKeyword(event: MatChipInputEvent): void {
    const rawKeyword = event.value?.trim();

    if (rawKeyword && !this.hasKeywordChip(rawKeyword)) {
      this.keywordChips = [...this.keywordChips, rawKeyword];
      this.syncKeywordControlValue();
      this.emitChange();
    }

    if (event.input) {
      event.input.value = '';
    }
  }

  removeKeyword(keyword: string): void {
    this.keywordChips = this.keywordChips.filter(
      (existingKeyword) => this.normalizeKeyword(existingKeyword) !== this.normalizeKeyword(keyword)
    );
    this.syncKeywordControlValue();
    this.emitChange();
  }

  selectMultiEnumOption(option: IEnumOption): void {
    if (this.selectedMultiEnumOptions.some((selected) => this.isSameOptionValue(selected.value, option.value))) {
      this.chipSearchControl.setValue('', { emitEvent: true });
      return;
    }

    this.selectedMultiEnumOptions = [...this.selectedMultiEnumOptions, option];
    this.mergeEnumOptions(this.selectedField?.key, [option]);
    this.syncMultiEnumValue();
    this.chipSearchControl.setValue('', { emitEvent: true });
    this.emitChange();
  }

  removeMultiEnumOption(option: IEnumOption): void {
    this.selectedMultiEnumOptions = this.selectedMultiEnumOptions.filter(
      (selected) => !this.isSameOptionValue(selected.value, option.value)
    );
    this.syncMultiEnumValue();
    this.emitChange();
    this.chipSearchControl.setValue(this.chipSearchControl.value, { emitEvent: true });
  }

  addMultiEnumFromInput(event: MatChipInputEvent): void {
    const rawValue = event.value?.trim().toLowerCase();

    if (rawValue && this.selectedField) {
      const matchedOption = (this.enumCache[this.selectedField.key] ?? []).find(
        (option) => option.label.toLowerCase() === rawValue
      );

      if (matchedOption) {
        this.selectMultiEnumOption(matchedOption);
      }
    }

    if (event.input) {
      event.input.value = '';
    }
    this.chipSearchControl.setValue('', { emitEvent: true });
  }

  displayEnumOption(option: string | IEnumOption | null): string {
    if (!option) {
      return '';
    }

    return typeof option === 'string' ? option : option.label;
  }

  get operatorOptions(): SelectOption[] {
    return this.availableOperators.map((op) => ({
      label: this.translate.instant(`workflows.operators.${op}`),
      value: op,
    }));
  }

  get enumOptions(): SelectOption[] {
    if (!this.selectedField?.enumEndpoint) return [];
    return (this.enumCache[this.selectedField.key] ?? []).map((o) => ({
      label: o.label,
      value: o.value,
    }));
  }

  get booleanOptions(): SelectOption[] {
    return [
      { label: this.translate.instant('common.yes'), value: true },
      { label: this.translate.instant('common.no'), value: false },
    ];
  }

  get isKeywordListOperator(): boolean {
    return this.isKeywordListOperatorValue(this.operatorControl.value);
  }

  get isEnumListOperator(): boolean {
    return this.selectedField?.dataType === 'enum'
      && LIST_OPERATORS.includes(this.operatorControl.value as ConditionOperator);
  }

  /**
   * Placeholder for the enum value select. Single-select auto-selects the first
   * item once the enum list loads, so it needs no placeholder then — but while the
   * options are still loading it shows the placeholder rather than a blank control.
   * Multi-select (in/not_in) defaults to empty, so it always keeps the placeholder.
   */
  get enumValuePlaceholder(): string {
    const showPlaceholder = this.isEnumListOperator || this.enumOptions.length === 0;
    return showPlaceholder ? this.translate.instant('workflows.builder.select_value') : '';
  }

  getFieldLabel(field: IConditionFieldDescriptor): string {
    return this.translate.instant(field.i18nLabel);
  }

  private rebuildConditionFieldGroups(): void {
    const categoryOrder: string[] = [];
    const groupedByCategory: Record<string, IConditionFieldDescriptor[]> = {};

    for (const field of this.conditionFields) {
      if (!groupedByCategory[field.category]) {
        groupedByCategory[field.category] = [];
        categoryOrder.push(field.category);
      }
      groupedByCategory[field.category].push(field);
    }

    this.conditionFieldGroups = categoryOrder.map((category) => ({
      label: this.getCategoryLabel(category),
      options: groupedByCategory[category],
    }));
  }

  private getCategoryLabel(category: string): string {
    const categoryLabels: Record<string, string> = {
      email: 'workflows.builder.condition_categories.email',
      conversation: 'workflows.builder.condition_categories.conversation',
      customer: 'workflows.builder.condition_categories.customer',
      order: 'workflows.builder.condition_categories.order',
    };

    const labelKey = categoryLabels[category];
    return labelKey ? this.translate.instant(labelKey) : category;
  }

  private updateFieldMeta(): void {
    this.selectedField =
      this.conditionFields.find((f) => f.key === this.fieldControl.value) ?? null;
    this.availableOperators = (this.selectedField?.operators ?? []) as ConditionOperator[];
    this.hideValue =
      this.operatorControl.value === 'is_empty' ||
      this.operatorControl.value === 'is_not_empty';
  }

  private emitChange(): void {
    this.conditionChanged.emit({
      ...this.condition,
      field: this.fieldControl.value,
      operator: this.operatorControl.value as ConditionOperator,
      value: this.getCurrentConditionValue(),
    });
  }

  private emitChangeIfStateDrifted(): void {
    const nextValue = this.getCurrentConditionValue();
    const hasDrift =
      this.condition.field !== this.fieldControl.value
      || this.condition.operator !== this.operatorControl.value
      || !this.isConditionValueEqual(this.condition.value, nextValue);

    if (hasDrift) {
      this.emitChange();
    }
  }

  private syncSelectBackedValue(): void {
    if (this.hideValue || !this.selectedField) {
      this.keywordChips = [];
      this.selectedMultiEnumOptions = [];
      return;
    }

    if (this.isKeywordListOperator) {
      this.syncKeywordChipsFromValue();
      this.syncKeywordControlValue();
      this.selectedMultiEnumOptions = [];
      return;
    }

    if (this.selectedField.dataType === 'multi_enum') {
      this.syncMultiEnumSelection();
      this.syncMultiEnumValue();
      return;
    }

    if (this.selectedField.dataType === 'boolean') {
      if (this.valueControl.value !== true && this.valueControl.value !== false) {
        this.valueControl.setValue(this.booleanOptions[0]?.value ?? true, { emitEvent: false });
      }
      return;
    }

    if (this.selectedField.dataType !== 'enum') {
      return;
    }

    if (this.isEnumListOperator) {
      const nextValue = this.normalizeEnumListValue(this.valueControl.value);

      if (!this.isConditionValueEqual(this.valueControl.value, nextValue)) {
        this.valueControl.setValue(nextValue, { emitEvent: false });
      }

      return;
    }

    if (Array.isArray(this.valueControl.value)) {
      this.valueControl.setValue(this.getEnumScalarValue(this.valueControl.value), { emitEvent: false });
    }

    const options = this.enumCache[this.selectedField.key] ?? [];
    if (!options.length) {
      return;
    }

    const hasMatchingValue = options.some((option) =>
      this.isSameOptionValue(option.value, this.valueControl.value)
    );

    if (!hasMatchingValue) {
      this.valueControl.setValue(options[0].value, { emitEvent: false });
    }
  }

  private getDefaultValueForSelectedField(): unknown {
    if (!this.selectedField || this.hideValue) {
      return '';
    }

    if (this.isKeywordListOperator) {
      return [];
    }

    if (this.selectedField.dataType === 'multi_enum') {
      return [];
    }

    if (this.selectedField.dataType === 'boolean') {
      return this.booleanOptions[0]?.value ?? true;
    }

    if (this.selectedField.dataType === 'enum') {
      if (this.isEnumListOperator) {
        return [];
      }

      return this.enumCache[this.selectedField.key]?.[0]?.value ?? '';
    }

    return '';
  }

  private isSameOptionValue(optionValue: string | number, currentValue: unknown): boolean {
    return optionValue === currentValue || String(optionValue) === String(currentValue);
  }

  private getCurrentConditionValue(): unknown {
    if (this.hideValue) {
      return null;
    }

    if (this.isKeywordListOperator) {
      return [...this.keywordChips];
    }

    if (this.selectedField?.dataType === 'multi_enum') {
      return this.selectedMultiEnumOptions.map((option) => option.value);
    }

    if (this.isEnumListOperator) {
      return this.normalizeEnumListValue(this.valueControl.value);
    }

    return this.valueControl.value;
  }

  private hasKeywordChip(keyword: string): boolean {
    const normalizedKeyword = this.normalizeKeyword(keyword);
    return this.keywordChips.some(
      (existingKeyword) => this.normalizeKeyword(existingKeyword) === normalizedKeyword
    );
  }

  private isKeywordListOperatorValue(operator: string): boolean {
    return KEYWORD_LIST_OPERATORS.includes(operator as ConditionOperator);
  }

  private normalizeEnumListValue(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (value == null || value === '') {
      return [];
    }

    return [value];
  }

  private getEnumScalarValue(value: unknown[]): unknown {
    return value[0] ?? this.enumCache[this.selectedField?.key ?? '']?.[0]?.value ?? '';
  }

  private loadMultiEnumSuggestions(search: string): Observable<IEnumOption[]> {
    if (!this.selectedField || this.selectedField.dataType !== 'multi_enum') {
      return of([]);
    }

    return this.workflowsService.searchEnumValues(this.selectedField.key, search).pipe(
      tap((options) => this.mergeEnumOptions(this.selectedField?.key, options)),
      map((options) =>
        options.filter(
          (option) =>
            !this.selectedMultiEnumOptions.some((selected) =>
              this.isSameOptionValue(selected.value, option.value)
            )
        )
      ),
      catchError(() => of([]))
    );
  }

  private syncMultiEnumSelection(): void {
    if (!this.selectedField || this.selectedField.dataType !== 'multi_enum') {
      this.selectedMultiEnumOptions = [];
      return;
    }

    const selectedValues = Array.isArray(this.valueControl.value) ? this.valueControl.value : [];
    const cachedOptions = this.enumCache[this.selectedField.key] ?? [];
    // Labels resolved server-side for the saved value(s); covers selections that
    // fall outside the search-capped enum list (e.g. products picked at creation).
    const savedOptions = this.condition.valueOptions ?? [];

    this.selectedMultiEnumOptions = selectedValues.map((value) => {
      return cachedOptions.find((option) => this.isSameOptionValue(option.value, value))
        ?? savedOptions.find((option) => this.isSameOptionValue(option.value, value))
        ?? { value: value as string | number, label: String(value) };
    });
  }

  private syncMultiEnumValue(): void {
    if (this.selectedField?.dataType !== 'multi_enum') {
      return;
    }

    const nextValue = this.selectedMultiEnumOptions.map((option) => option.value);

    if (!this.isConditionValueEqual(this.valueControl.value, nextValue)) {
      this.valueControl.setValue(nextValue, { emitEvent: false });
    }
  }

  private syncKeywordChipsFromValue(): void {
    const value = Array.isArray(this.valueControl.value) ? this.valueControl.value : [];
    this.keywordChips = value
      .map((entry) => String(entry).trim())
      .filter((entry, index, keywords) => !!entry && keywords.indexOf(entry) === index);
  }

  private syncKeywordControlValue(): void {
    const nextValue = [...this.keywordChips];

    if (!this.isConditionValueEqual(this.valueControl.value, nextValue)) {
      this.valueControl.setValue(nextValue, { emitEvent: false });
    }
  }

  private mergeEnumOptions(fieldKey: string | undefined, options: IEnumOption[]): void {
    if (!fieldKey || !options.length) {
      return;
    }

    const existingOptions = this.enumCache[fieldKey] ?? [];
    const mergedOptions = [...existingOptions];

    for (const option of options) {
      const alreadyExists = mergedOptions.some((existingOption) =>
        this.isSameOptionValue(existingOption.value, option.value)
      );

      if (!alreadyExists) {
        mergedOptions.push(option);
      }
    }

    this.enumCache = { ...this.enumCache, [fieldKey]: mergedOptions };
  }

  private normalizeMultiEnumSearch(value: string | IEnumOption): string {
    return typeof value === 'string' ? value.trim() : value?.label?.trim() ?? '';
  }

  private normalizeKeyword(value: string): string {
    return value.trim().toLowerCase();
  }

  private isConditionValueEqual(previousValue: unknown, nextValue: unknown): boolean {
    if (Array.isArray(previousValue) || Array.isArray(nextValue)) {
      if (!Array.isArray(previousValue) || !Array.isArray(nextValue)) {
        return false;
      }

      if (previousValue.length !== nextValue.length) {
        return false;
      }

      return previousValue.every((entry, index) =>
        this.isSameOptionValue(entry as string | number, nextValue[index])
      );
    }

    return previousValue === nextValue;
  }
}
